import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Modal,
  Stack,
  Divider,
  IconButton,
  Button,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatMoney } from "helpers/types";
import { containerBorderSx, legRowSx } from "styles/RedirectionModal.styles";
import {
  useTrading,
  useFactsetStreaming,
  useAppDispatch,
} from "features/store";
import { useFDSWebSocket } from "hooks/useFDSWebSocket";
import {
  FDS_SubscriptionTypes,
  NOTISTACK_ALERT_OPTIONS,
} from "constants/index";
import dayjs from "dayjs";
import { getFDSOptionData } from "utils/fdsUtils";
import { useNewCallMutation } from "features/trading/accounts";
import { setCurrentStrategy } from "features/trading/tradingSlice";
import { enqueueSnackbar, closeSnackbar } from "notistack";

interface ApplyLegsModalProps {
  isStrategyLoaded?: boolean;
}

export const ApplyLegsModal = ({
  isStrategyLoaded = true,
}: ApplyLegsModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const { currentStrategy } = useTrading();
  const {
    subscriptions,
    dataCache: { [FDS_SubscriptionTypes.OPTION_CONTRACT]: FDS_optionData },
  } = useFactsetStreaming();
  const dispatch = useAppDispatch();
  const [newCall, newCallRes] = useNewCallMutation();

  // Handle newCall response
  useEffect(() => {
    const { data, isSuccess } = newCallRes;
    if (isSuccess && data) {
      dispatch(setCurrentStrategy(data));
      closeSnackbar();
    }
  }, [newCallRes, dispatch]);

  // Parse legs from query params
  const parsedLegs = useMemo(() => {
    const legIndices = new Set<number>();

    // Find all leg indices
    searchParams.forEach((value, key) => {
      const match = key.match(
        /legs\[(\d+)\]\.(action|strike_price|expiration_date)/,
      );
      if (match) {
        legIndices.add(parseInt(match[1]));
      }
    });

    // Build legs array (sorted by index)
    const sortedIndices = Array.from(legIndices).sort((a, b) => a - b);
    return sortedIndices.map((idx) => {
      const action = searchParams.get(`legs[${idx}].action`);
      const strikePrice = searchParams.get(`legs[${idx}].strike_price`);
      const expirationDate = searchParams.get(`legs[${idx}].expiration_date`);

      return {
        action: action || undefined,
        strike_price: strikePrice || undefined,
        expiration_date: expirationDate || undefined,
      };
    });
  }, [searchParams]);

  // Build option symbols from parsed legs with symbol mapping
  const { optionSymbols, legsWithSymbols } = useMemo(() => {
    if (!currentStrategy?.name || parsedLegs.length === 0) {
      return { optionSymbols: [], legsWithSymbols: [] };
    }

    const symbols: string[] = [];
    const legsWithSym = parsedLegs
      .filter((leg) => leg.expiration_date && leg.strike_price)
      .map((leg) => {
        const underlying = currentStrategy.name;
        const paddedUnderlying = underlying.padEnd(6, " ");
        const expDate = dayjs(leg.expiration_date).format("YYMMDD");
        const strikePriceNum = parseFloat(leg.strike_price || "0");
        const formattedStrike = Number(strikePriceNum.toFixed(2))
          .toFixed(3)
          .replace(".", "")
          .padStart(8, "0");
        // call_or_put: false = Call (C), true = Put (P)
        const optionType = currentStrategy.call_or_put ? "P" : "C";
        const symbol =
          `${paddedUnderlying}${expDate}${optionType}${formattedStrike}-USA`.replace(
            / +/g,
            "#",
          );
        symbols.push(symbol);
        return { ...leg, optionSymbol: symbol };
      });

    return { optionSymbols: symbols, legsWithSymbols: legsWithSym };
  }, [parsedLegs, currentStrategy]);

  // Build option subscription key for FDS
  const optionSubKey = useMemo(() => {
    if (optionSymbols.length === 0) {
      return null;
    }
    return `${FDS_SubscriptionTypes.OPTION_CONTRACT}:${optionSymbols.join(",")}`;
  }, [optionSymbols]);

  // Calculate optionData for each leg using useMemo (outside rendering)
  const legsWithOptionData = useMemo(() => {
    if (!currentStrategy || legsWithSymbols.length === 0) {
      return [];
    }

    const optionType = currentStrategy.call_or_put ? "P" : "C";

    return legsWithSymbols.map((leg) => {
      const optionName = leg.optionSymbol;
      const optionData = optionName
        ? getFDSOptionData(optionName, FDS_optionData)
        : null;

      return {
        ...leg,
        optionName,
        optionData,
      };
    });
  }, [legsWithSymbols, currentStrategy, FDS_optionData]);

  // Check if all legs have FDS data
  const allLegsHaveFDSData = useMemo(() => {
    if (legsWithOptionData.length === 0) {
      return false;
    }
    return legsWithOptionData.every(
      (leg) =>
        leg.optionData?.data !== undefined && leg.optionData?.data !== null,
    );
  }, [legsWithOptionData]);

  // Connect to FDS WebSocket when we have option symbols and modal is showing
  useFDSWebSocket(
    null,
    optionSubKey,
    subscriptions,
    FDS_SubscriptionTypes.OPTION_CONTRACT,
    dispatch,
  );

  // Show modal when legs params are present and strategy is loaded (if URL params were provided)
  useEffect(() => {
    if (parsedLegs.length > 0 && isStrategyLoaded) {
      setShowModal(true);
    }
  }, [parsedLegs.length, isStrategyLoaded]);

  const handleConfirm = useCallback(async () => {
    if (!currentStrategy || legsWithOptionData.length === 0) {
      return;
    }

    if (currentStrategy.current_call) {
      enqueueSnackbar(
        "Current call already exists",
        NOTISTACK_ALERT_OPTIONS as any,
      );
      setShowModal(false);
      setSearchParams({});
      return;
    }

    const asOfDate = dayjs().format("YYYY-MM-DD");

    // Create new call for each leg
    for (const legWithData of legsWithOptionData) {
      if (!legWithData.expiration_date || !legWithData.strike_price) {
        continue;
      }

      // Use symbolKey from optionData, fallback to optionSymbol if not available
      const symbol =
        legWithData.optionData?.symbolKey || legWithData.optionSymbol || "";

      try {
        await newCall({
          id: currentStrategy.id,
          as_of_date: asOfDate,
          name: legWithData.optionName || "",
          symbol: symbol,
        }).unwrap();
      } catch (error) {
        console.error("Error creating new call:", error);
        // Continue with other legs even if one fails
      }
    }

    setShowModal(false);
    // Clear query params after confirming
    setSearchParams({});
  }, [currentStrategy, legsWithOptionData, newCall, setSearchParams]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setSearchParams({});
    closeSnackbar();
  }, [setSearchParams]);

  if (!showModal) return null;

  return (
    <Modal
      open={showModal}
      onClose={handleCancel}
      aria-labelledby="apply-legs-modal-title"
      aria-describedby="apply-legs-modal-desc"
    >
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        className="px-4"
      >
        <Box sx={{ width: "100%", maxWidth: 600 }}>
          <Card>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Typography
                id="apply-legs-modal-title"
                variant="h6"
                component="h2"
              >
                Apply Strategy Data
              </Typography>
              <IconButton
                aria-label="Close"
                onClick={handleCancel}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Divider />
            <Box id="apply-legs-modal-desc" sx={{ p: 2 }}>
              <Box sx={containerBorderSx}>
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: 16, mb: 0.5, color: "#6c757d" }}>
                    Would you like to apply the following legs on this stock?
                  </Typography>
                  <Box sx={{ height: 12 }} />
                  <Typography sx={{ fontSize: 16, mb: 0.5, color: "#6c757d" }}>
                    Legs:
                  </Typography>
                  <Stack spacing={1}>
                    {legsWithOptionData.length === 0 ? (
                      <Typography sx={{ p: 1, color: "#6c757d" }}>
                        No legs
                      </Typography>
                    ) : (
                      legsWithOptionData.map((legWithData, idx) => {
                        const action = (legWithData.action || "").toUpperCase();
                        const isBuy = action === "BUY";
                        const isSell = action === "SELL";
                        const badgeBg = isBuy
                          ? "#00ff0040"
                          : isSell
                            ? "#fb923c"
                            : "#e5e7eb";
                        const badgeFg = "#000000";
                        const expText = legWithData.expiration_date || "—";
                        const strikeFmt =
                          !legWithData.strike_price ||
                          legWithData.strike_price === ""
                            ? "—"
                            : formatMoney(Number(legWithData.strike_price), 4);

                        const optionData = legWithData.optionData;
                        const lastPrice = optionData?.lastPrice || "N/A";
                        const lastPriceDisplay =
                          lastPrice !== "N/A" && lastPrice !== undefined
                            ? `$${parseFloat(lastPrice).toFixed(2)}`
                            : "N/A";

                        return (
                          <Stack
                            key={`${idx}-${expText}-${strikeFmt}`}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={legRowSx}
                          >
                            <Typography sx={{ color: "#6c757d" }}>
                              {currentStrategy?.name}
                            </Typography>
                            <Chip
                              label={action || "—"}
                              size="small"
                              sx={{
                                bgcolor: badgeBg,
                                color: badgeFg,
                                fontWeight: 700,
                                fontSize: 12,
                              }}
                            />
                            <Typography sx={{ color: "#6c757d" }}>
                              {expText}
                            </Typography>
                            <Typography sx={{ color: "#6c757d" }}>@</Typography>
                            <Typography sx={{ color: "#495057" }}>
                              {strikeFmt}
                            </Typography>
                            <Typography
                              sx={{
                                color: "#495057",
                                fontWeight: 500,
                                ml: 1,
                              }}
                            >
                              (Last price: {lastPriceDisplay})
                            </Typography>
                          </Stack>
                        );
                      })
                    )}
                  </Stack>
                  <Box sx={{ height: 16 }} />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={handleCancel} color="inherit">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirm}
                      color="primary"
                      variant="contained"
                      autoFocus
                      disabled={newCallRes.isLoading || !allLegsHaveFDSData}
                    >
                      {newCallRes.isLoading ? "Applying..." : "Apply"}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
      </Stack>
    </Modal>
  );
};
